import React from 'react';
import styled, { useTheme, css } from 'styled-components';
import { sizes } from 'style';
import PropTypes from 'prop-types';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { FORBIDDEN_DESTINATIONS_SW } from 'products/nomad_insurance/constants';

const iso = require('iso-3166-1');

const StyledGeography = styled(Geography)`
  cursor: pointer;
  pointer-events: none;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      pointer-events: none;
    `}

  @media ${sizes.tabletPortrait} {
    pointer-events: auto;
  }
`;

const GEO_URL_WORLD = `${process.env.PUBLIC_URL}/worldmap.json`;

const WorldMap = ({
  className,
  setTooltipContent,
  onChange,
  countries,
  homeCountry,
  citizenshipCountry
}) => {
  const theme = useTheme();

  const forbiddenDestinations = FORBIDDEN_DESTINATIONS_SW;

  return (
    <ComposableMap
      projectionConfig={{
        scale: 155,
        center: [22, 8]
      }}
      data-tip=""
      width={730}
      height={370}
      className={className}
    >
      <Geographies geography={GEO_URL_WORLD}>
        {({ geographies }) =>
          geographies
            .filter((d) => d.properties.REGION_UN !== 'Antarctica')
            .map((geo) => {
              geo.properties.isoCode = iso.whereNumeric(geo.id)?.alpha2;

              // handle countries that are on the map but not internationally
              // recognised -> don't have their own iso code
              if (geo.properties.name === 'Somaliland') {
                geo.properties.isoCode = iso.whereCountry('SOMALIA')?.alpha2;
              }
              if (geo.properties.name === 'Kosovo') {
                geo.properties.isoCode = 'XK';
              }
              if (geo.properties.name === 'N. Cyprus') {
                geo.properties.isoCode = 'CY';
              }

              let isRestrictedCountry = forbiddenDestinations.includes(
                geo.properties.isoCode
              );

              if (
                (citizenshipCountry === 'US' || homeCountry === 'US') &&
                ['US', 'PR', 'VI'].includes(geo.properties.isoCode)
              ) {
                isRestrictedCountry = true;
              }

              const style = {
                default: {
                  outline: 'none',
                  fill: theme.colors['fountainBlue+2']
                },
                hover: {
                  fill: theme.colors['fountainBlue+1'],
                  outline: 'none'
                },
                pressed: {
                  fill: theme.colors.lightYellow,
                  outline: 'none'
                }
              };

              if (countries.includes(geo.properties.isoCode)) {
                style.default.fill = theme.colors.fountainBlue;
              }

              if (isRestrictedCountry) {
                style.default.fill = theme.colors.pickledBluewood35;
                style.hover.fill = theme.colors.fuzzyWuzzyBrown;
                style.pressed.fill = theme.colors.fuzzyWuzzyBrown;
              }

              if (geo.properties.isoCode === homeCountry) {
                style.default.fill = theme.colors['confetti+2'];
                style.hover.fill = theme.colors.fuzzyWuzzyBrown;
                style.pressed.fill = theme.colors.fuzzyWuzzyBrown;
              }

              return (
                <StyledGeography
                  key={geo.rsmKey}
                  geography={geo}
                  disabled={isRestrictedCountry}
                  onMouseEnter={() => {
                    if (geo.properties.isoCode === homeCountry) {
                      setTooltipContent(
                        `You cannot select your home country as a destination. We can cover you for brief visits up to ${
                          geo.properties.isoCode === 'US' ? 15 : 30
                        } days in your home country for every 90 days you spend abroad. Learn more about exclusions above.`
                      );
                      return;
                    }
                    const { name } = geo.properties;
                    if (isRestrictedCountry) {
                      setTooltipContent(
                        `Sorry, we don't offer coverage in ${name} at this time. Learn more about exclusions above.`
                      );
                      return;
                    }
                    if (name === 'Somaliland') {
                      setTooltipContent('Somalia');
                      return;
                    }
                    setTooltipContent(name);
                  }}
                  onMouseLeave={() => {
                    setTooltipContent('');
                  }}
                  onClick={() => {
                    if (!isRestrictedCountry) {
                      const { isoCode, name } = geo.properties;
                      onChange(isoCode, name);
                    }
                  }}
                  stroke={theme.colors.white}
                  strokeWidth={0.2}
                  style={style}
                />
              );
            })
        }
      </Geographies>
    </ComposableMap>
  );
};

WorldMap.propTypes = {
  className: PropTypes.string,
  setTooltipContent: PropTypes.func,
  onChange: PropTypes.func,
  countries: PropTypes.array,
  homeCountry: PropTypes.string,
  citizenshipCountry: PropTypes.string
};

export default WorldMap;
